"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const L = {
    fr: {
        title: "Une heure de votre automatisation",
        polling: "Sondage",
        webhook: "Webhook",
        every: "Vérification toutes les",
        min: "min",
        hour: "1 h",
        silence: "Aucun appel : rien ne se passe, donc rien n'est envoyé.",
        perHour: "appels par heure",
        perMonth: "appels par mois",
        useful: "utiles",
        wasted: "gaspillés",
        verdictPolling: "Vous payez {n} appels pour trouver ce qu'un seul message vous aurait dit.",
        verdictWebhook: "Un appel, au moment exact où l'événement se produit.",
        note: "Sur la base de dix événements réels par mois, la fréquence des occasions où quelque chose arrive vraiment dans une petite structure.",
    },
    en: {
        title: "One hour of your automation",
        polling: "Polling",
        webhook: "Webhook",
        every: "Check every",
        min: "min",
        hour: "1 h",
        silence: "No calls: nothing happens, so nothing is sent.",
        perHour: "calls per hour",
        perMonth: "calls per month",
        useful: "useful",
        wasted: "wasted",
        verdictPolling: "You pay for {n} calls to find what a single message would have told you.",
        verdictWebhook: "One call, at the exact moment the event happens.",
        note: "Based on ten real events per month, the rate at which something actually happens in a small business.",
    },
};

const INTERVALS = [1, 5, 15, 60];
const EVENTS_PER_MONTH = 10;

export default function PollingVsWebhook({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    const [mode, setMode] = useState("polling");
    const [interval, setIntervalValue] = useState(5);

    const r = useMemo(() => {
        const perHour = mode === "polling" ? 60 / interval : 0;
        const perMonth =
            mode === "polling" ? Math.round(perHour * 24 * 30) : EVENTS_PER_MONTH;

        return { perHour, perMonth };
    }, [mode, interval]);

    const dots = useMemo(() => {
        if (mode === "webhook") return [];
        return Array.from({ length: 60 / interval }, () => ({}));
    }, [mode, interval]);

    const big = (v) => v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US");

    return (
        <div className="my-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5 sm:p-6 text-base leading-normal">
            <div className="flex items-center gap-2 mb-5">
                <SolarIcon name="clock-circle-linear" className="w-5 h-5 text-primary" />
                <div className="font-bold dark:text-white text-base leading-snug">{t.title}</div>
            </div>

            {/* Interrupteur entre les deux approches */}
            <div className="inline-flex rounded-xl border border-gray-200 dark:border-white/10 p-1 mb-5">
                {["polling", "webhook"].map((m) => (
                    <button
                        key={m}
                        type="button"
                        onClick={() => setMode(m)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                            mode === m
                                ? "bg-primary text-white"
                                : "text-gray-600 dark:text-lightBlue hover:text-gray-900 dark:hover:text-white"
                        }`}
                    >
                        {t[m]}
                    </button>
                ))}
            </div>

            {mode === "polling" && (
                <div className="flex items-center gap-2 mb-5 flex-wrap">
                    <span className="text-sm text-gray-600 dark:text-lightBlue">
                        {t.every}
                    </span>
                    {INTERVALS.map((i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => setIntervalValue(i)}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                                interval === i
                                    ? "border-primary text-primary bg-primary/10"
                                    : "border-gray-200 dark:border-white/10 text-gray-600 dark:text-lightBlue"
                            }`}
                        >
                            {i === 60 ? t.hour : `${i} ${t.min}`}
                        </button>
                    ))}
                </div>
            )}

            {/* Une heure, un point par appel */}
            <div className="flex flex-wrap gap-1.5 p-4 rounded-xl bg-gray-50 dark:bg-white/[0.02] min-h-[4.5rem] items-center">
                {dots.length === 0 ? (
                    <span className="text-sm text-gray-600 dark:text-lightBlue">
                        {t.silence}
                    </span>
                ) : (
                    dots.map((_, i) => (
                        <span
                            key={i}
                            className="w-3 h-3 rounded-full bg-gray-300 dark:bg-white/15"
                        />
                    ))
                )}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-5">
                <div className="rounded-xl bg-primary/[0.06] dark:bg-primary/10 p-4">
                    <div className="text-3xl font-bold text-primary tabular-nums">
                        {big(r.perHour)}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-lightBlue mb-0">
                        {t.perHour}
                    </div>
                </div>
                <div className="rounded-xl bg-primary/[0.06] dark:bg-primary/10 p-4">
                    <div className="text-3xl font-bold text-primary tabular-nums">
                        {big(r.perMonth)}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-lightBlue mb-0">
                        {t.perMonth}
                        {mode === "polling"
                            ? ` · ${EVENTS_PER_MONTH} ${t.useful}`
                            : ` · 100 % ${t.useful}`}
                    </div>
                </div>
            </div>

            <div
                className={`mt-4 text-sm font-semibold mb-0 ${
                    mode === "polling"
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-green-700 dark:text-green-400"
                }`}
            >
                {mode === "polling"
                    ? t.verdictPolling.replace("{n}", big(r.perMonth))
                    : t.verdictWebhook}
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0">
                {t.note}
            </p>
        </div>
    );
}
